import proj4 from "proj4"
import { toPoint as mgrsToPoint } from "mgrs"
import {
  COORDINATE_FORMAT_OPTIONS,
  formatCoordinatePair,
  toDDM,
} from "./coordinateFormat"

export { toDDM } from "./coordinateFormat"

function parseUtm(text, strict = false) {
  const upper = text.toUpperCase()
  const completeMatch = upper.match(/^(?:ZONE\s*)?(\d{1,2})([C-HJ-NP-X])?\s+EASTING\s*[:=]?\s*(\d+(?:\.\d+)?)\s+NORTHING\s*[:=]?\s*(\d+(?:\.\d+)?)$/)
  if (strict && !completeMatch) return null

  const zoneMatch = completeMatch || upper.match(/(?:ZONE\s*)?(\d{1,2})([C-HJ-NP-X])?\b/)
  const eastingMatch = completeMatch
    ? { 1: completeMatch[3] }
    : upper.match(/EASTING\s*[:=]?\s*(\d+(?:\.\d+)?)/)
  const northingMatch = completeMatch
    ? { 1: completeMatch[4] }
    : upper.match(/NORTHING\s*[:=]?\s*(\d+(?:\.\d+)?)/)

  if (!zoneMatch || !eastingMatch || !northingMatch) return null

  const zone = Number(zoneMatch[1])
  const zoneBand = zoneMatch[2] || ""
  if (!(zone >= 1 && zone <= 60)) return { error: `Invalid UTM zone: ${zone}` }

  const easting = Number(eastingMatch[1])
  const northing = Number(northingMatch[1])

  const isNorth = zoneBand ? zoneBand >= "N" : northing < 10000000
  const epsg = isNorth ? `EPSG:326${String(zone).padStart(2, "0")}` : `EPSG:327${String(zone).padStart(2, "0")}`

  try {
    const [lng, lat] = proj4(epsg, "EPSG:4326", [easting, northing])
    return { lat, lng }
  } catch {
    return { error: "Could not convert UTM coordinate to WGS84." }
  }
}

function parseMgrs(text, strict = false) {
  const trimmed = text.trim()
  const complete = /^(?:MGRS\s*)?\d{1,2}[C-HJ-NP-X]\s*[A-Z]{2}\s*\d+\s*\d+$/i.test(trimmed)
  if (strict && !complete) return null

  const cleaned = trimmed
    .toUpperCase()
    .replace(/\bMGRS\b/g, "")
    .replace(/[^0-9A-Z ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()

  const m = cleaned.match(/^\d{1,2}[C-HJ-NP-X]\s*[A-Z]{2}\s*\d+\s*\d+$/)
  if (!m) return null

  const compact = cleaned.replace(/\s+/g, "")
  try {
    const [lng, lat] = mgrsToPoint(compact)
    return { lat, lng }
  } catch {
    return { error: "Could not convert MGRS coordinate to WGS84." }
  }
}

function hasStrictCoordinateTokenSyntax(raw, isLat) {
  const input = String(raw || "").trim().toUpperCase()
  const degreeDigits = isLat ? 2 : 3
  const compactDmsDigits = degreeDigits + 4
  const compactDdmDigits = degreeDigits + 2

  if (/^[+-]?\d+(?:\.\d+)?\s+RAD(?:IANS)?$/.test(input)) return true

  const unsignedNumber = "\\d+(?:\\.\\d+)?"
  const signedNumber = `[+-]?${unsignedNumber}`
  const withOptionalHemisphere = (unsignedBody, signedBody = unsignedBody) => (
    `(?:${signedBody}|[NSEW]\\s*${unsignedBody}|${unsignedBody}\\s*[NSEW])`
  )
  const compactDms = withOptionalHemisphere(
    `\\d{${compactDmsDigits}}(?:\\.\\d+)?`,
    `[+-]?\\d{${compactDmsDigits}}(?:\\.\\d+)?`,
  )
  const compactDdm = withOptionalHemisphere(`\\d{${compactDdmDigits}}(?:\\.\\d+)?`)
  if (new RegExp(`^${compactDms}$`).test(input)) return true
  if (new RegExp(`^${compactDdm}$`).test(input)) return true

  const degreeMarker = "(?:°|DEG(?:REE)?S?)"
  const minuteMarker = "(?:['′]|MIN(?:UTE)?S?)"
  const secondMarker = "(?:''|[\"″]|SEC(?:OND)?S?)"
  const degreeSeparator = `(?:\\s*${degreeMarker}\\s*|\\s+)`
  const minuteSeparator = `(?:\\s*${minuteMarker}\\s*|\\s+)`
  const dmsBody = `${unsignedNumber}${degreeSeparator}${unsignedNumber}${minuteSeparator}${unsignedNumber}(?:\\s*${secondMarker})?`
  const signedDmsBody = `${signedNumber}${degreeSeparator}${unsignedNumber}${minuteSeparator}${unsignedNumber}(?:\\s*${secondMarker})?`
  const ddmBody = `${unsignedNumber}${degreeSeparator}${unsignedNumber}(?:\\s*${minuteMarker})?`
  const signedDdmBody = `${signedNumber}${degreeSeparator}${unsignedNumber}(?:\\s*${minuteMarker})?`
  const ddBody = `${unsignedNumber}(?:\\s*${degreeMarker})?`
  const signedDdBody = `${signedNumber}(?:\\s*${degreeMarker})?`
  const dms = new RegExp(`^${withOptionalHemisphere(dmsBody, signedDmsBody)}$`)
  const ddm = new RegExp(`^${withOptionalHemisphere(ddmBody, signedDdmBody)}$`)
  const dd = new RegExp(`^${withOptionalHemisphere(ddBody, signedDdBody)}$`)
  return dms.test(input) || ddm.test(input) || dd.test(input)
}

function parseCoordToken(raw, isLat, strict = false) {
  const input = String(raw || "").trim().toUpperCase()
  if (!input) return { error: "Missing coordinate value." }
  if (strict && !hasStrictCoordinateTokenSyntax(input, isLat)) {
    return { error: `Invalid coordinate syntax: ${raw}` }
  }

  if (input.includes("RAD")) {
    const n = Number(input.replace(/[^0-9+\-.]/g, ""))
    if (!Number.isFinite(n)) return { error: `Could not parse radians value: ${raw}` }
    return { value: (n * 180) / Math.PI }
  }

  const hemiPrefix = input.match(/^([NSEW])(?=\s*[+-]?\d)/)?.[1] || ""
  const hemiSuffix = input.match(/(?:\s|[0-9°'"′″])([NSEW])$/)?.[1] || ""
  const hemi = hemiPrefix || hemiSuffix
  const sign = /-/.test(input) || hemi === "S" || hemi === "W" ? -1 : 1

  const compactDms = input.match(/^([NSEW])?([+-]?)(\d{6,7}(?:\.\d+)?)([NSEW])?$/)
  if (compactDms) {
    const num = compactDms[3]
    const degDigits = isLat ? 2 : 3
    const expectedDigits = isLat ? 6 : 7
    if (num.split(".")[0].length !== expectedDigits) return { error: `Could not parse ${raw}` }

    const deg = Number(num.slice(0, degDigits))
    const min = Number(num.slice(degDigits, degDigits + 2))
    const sec = Number(num.slice(degDigits + 2))
    if (!Number.isFinite(deg) || !Number.isFinite(min) || !Number.isFinite(sec)) return { error: `Could not parse ${raw}` }
    if (min >= 60 || sec >= 60) return { error: `Invalid DMS value in ${raw}` }
    const compactSign = compactDms[2] === "-" || hemi === "S" || hemi === "W" ? -1 : 1
    return { value: compactSign * (deg + min / 60 + sec / 3600) }
  }

  const compact = input.match(/^([NSEW])?(\d{4,5}(?:\.\d+)?)([NSEW])?$/)
  if (compact) {
    const num = compact[2]
    const degDigits = isLat ? 2 : 3
    const deg = Number(num.slice(0, degDigits))
    const min = Number(num.slice(degDigits))
    if (!Number.isFinite(deg) || !Number.isFinite(min)) return { error: `Could not parse ${raw}` }
    if (min >= 60) return { error: `Invalid DDM value in ${raw}` }
    return { value: sign * (deg + min / 60) }
  }

  const nums = input.match(/[+-]?\d+(?:\.\d+)?/g) || []
  if (!nums.length) return { error: `Could not parse coordinate from: ${raw}` }

  let value
  if (nums.length >= 3 || /[″"]/.test(input) || /\bSEC\b/.test(input)) {
    const d = Number(nums[0])
    const m = Number(nums[1])
    const s = Number(nums[2])
    if (!Number.isFinite(m) || !Number.isFinite(s) || m >= 60 || s >= 60) {
      return { error: `Invalid DMS value in ${raw}` }
    }
    value = Math.abs(d) + m / 60 + s / 3600
  } else if (nums.length >= 2 || /[′']/.test(input) || /\bMIN\b/.test(input)) {
    const d = Number(nums[0])
    const m = Number(nums[1])
    if (!Number.isFinite(m) || m >= 60) return { error: `Invalid DDM value in ${raw}` }
    value = Math.abs(d) + m / 60
  } else {
    value = Math.abs(Number(nums[0]))
  }

  if (!Number.isFinite(value)) return { error: `Could not parse coordinate from: ${raw}` }
  return { value: sign * value }
}

function validateLatLng(lat, lng) {
  if (lat < -90 || lat > 90) return `Latitude out of range (-90..90): ${lat}`
  if (lng < -180 || lng > 180) return `Longitude out of range (-180..180): ${lng}`
  return ""
}

function splitCoordinateParts(text) {
  if (text.includes(",")) {
    return text.split(",").map((part) => part.trim()).filter(Boolean).slice(0, 2)
  }

  const hemiMatches = text.match(/[NSEW][^NSEW,;]*|[^NSEW,;]*[NSEW]/gi)
  if (hemiMatches && hemiMatches.length >= 2) {
    return [hemiMatches[0].trim(), hemiMatches[1].trim()]
  }

  const cleaned = text.replace(/LAT\.?/gi, "").replace(/LONG\.?/gi, "")
  const matches = cleaned.match(/[NSEW]?[+-]?\d+(?:\.\d+)?(?:\s*[°'"′″]\s*\d+(?:\.\d+)?)?(?:\s*[°'"′″]\s*\d+(?:\.\d+)?)?[NSEW]?/gi) || []
  return matches.slice(0, 2).map((part) => part.trim())
}

function strictTokenPair(first, second) {
  return hasStrictCoordinateTokenSyntax(first, true) && hasStrictCoordinateTokenSyntax(second, false)
    ? [first.trim(), second.trim()]
    : null
}

function splitStrictCoordinateParts(text) {
  const commaParts = text.split(",")
  if (commaParts.length === 2) {
    const [firstRaw, secondRaw] = commaParts.map((part) => part.trim())
    const verboseLat = firstRaw.match(/^BEGINNING\s+AT\s+LAT\.?\s+(.+?)\.?$/i)
    const labelledLat = firstRaw.match(/^LAT\.?\s+(.+)$/i)
    const labelledLng = secondRaw.match(/^LONG\.?\s+(.+)$/i)

    if (verboseLat || labelledLat || labelledLng) {
      if ((!verboseLat && !labelledLat) || !labelledLng) return null
      return strictTokenPair((verboseLat || labelledLat)[1], labelledLng[1])
    }
    return strictTokenPair(firstRaw, secondRaw)
  }
  if (commaParts.length > 2) return null

  const labelled = text.match(/^(?:BEGINNING\s+AT\s+)?LAT\.?\s+(.+?)\s+LONG\.?\s+(.+)$/i)
  if (labelled) return strictTokenPair(labelled[1], labelled[2])
  if (/\b(?:LAT|LONG)\.?\b/i.test(text)) return null

  const ddPair = text.match(/^([+-]?\d+(?:\.\d+)?(?:\s*°)?)\s+([+-]?\d+(?:\.\d+)?(?:\s*°)?)$/)
  if (ddPair) return strictTokenPair(ddPair[1], ddPair[2])

  for (const separator of text.matchAll(/\s+/g)) {
    const first = text.slice(0, separator.index)
    const second = text.slice(separator.index + separator[0].length)
    if (!/(?:^[NSEW]|[NSEW]$)/i.test(first) || !/(?:^[NSEW]|[NSEW]$)/i.test(second)) continue
    const pair = strictTokenPair(first, second)
    if (pair) return pair
  }

  if (!/\s/.test(text) && /[NSEW]/i.test(text)) {
    for (let index = 1; index < text.length; index += 1) {
      const pair = strictTokenPair(text.slice(0, index), text.slice(index))
      if (pair) return pair
    }
  }

  return null
}

function strictSyntaxError(text) {
  return { error: `Unconsumed coordinate text: ${text}` }
}

export function parseCoordinateLine(line, { strict = false } = {}) {
  const text = String(line || "").trim()
  if (!text) return { error: "Empty coordinate line." }

  const utm = parseUtm(text, strict)
  if (utm) {
    if (utm.error) return { error: utm.error }
    const rangeError = validateLatLng(utm.lat, utm.lng)
    if (rangeError) return { error: rangeError }
    return { lat: utm.lat, lng: utm.lng, ddm: `${toDDM(utm.lat, true)}, ${toDDM(utm.lng, false)}` }
  }

  const mgrs = parseMgrs(text, strict)
  if (mgrs) {
    if (mgrs.error) return { error: mgrs.error }
    const rangeError = validateLatLng(mgrs.lat, mgrs.lng)
    if (rangeError) return { error: rangeError }
    return { lat: mgrs.lat, lng: mgrs.lng, ddm: `${toDDM(mgrs.lat, true)}, ${toDDM(mgrs.lng, false)}` }
  }

  const parts = strict ? splitStrictCoordinateParts(text) : splitCoordinateParts(text)

  if (!parts || parts.length < 2) {
    return strict ? strictSyntaxError(text) : { error: `Expected latitude and longitude in line: ${text}` }
  }

  const latParsed = parseCoordToken(parts[0], true, strict)
  if (latParsed.error) return { error: `Latitude error: ${latParsed.error}` }

  const lngParsed = parseCoordToken(parts[1], false, strict)
  if (lngParsed.error) return { error: `Longitude error: ${lngParsed.error}` }

  const lat = latParsed.value
  const lng = lngParsed.value

  const rangeError = validateLatLng(lat, lng)
  if (rangeError) return { error: rangeError }

  return { lat, lng, ddm: `${toDDM(lat, true)}, ${toDDM(lng, false)}` }
}

export function convertCoordinateFormats(input) {
  const parsed = parseCoordinateLine(input)
  if (parsed.error) return { error: parsed.error, formats: [] }

  const { lat, lng } = parsed
  const formats = COORDINATE_FORMAT_OPTIONS.map(({ value, shortLabel }) => ({
    key: value,
    label: shortLabel,
    value: formatCoordinatePair(lat, lng, value),
  }))

  return { lat, lng, formats }
}

export function parseCoordinateListDetailed(text) {
  const lines = String(text || "").split("\n")
  const parsedLines = []
  const coords = []

  lines.forEach((raw, idx) => {
    const lineNo = idx + 1
    const line = raw.trim()
    if (!line) return

    const parsed = parseCoordinateLine(line)
    if (parsed.error) {
      parsedLines.push({ lineNo, input: line, ok: false, error: parsed.error })
      return
    }

    parsedLines.push({ lineNo, input: line, ok: true, ddm: parsed.ddm })
    coords.push([parsed.lng, parsed.lat])
  })

  const firstError = parsedLines.find((l) => !l.ok)
  return {
    coords,
    parsedLines,
    error: firstError ? `Line ${firstError.lineNo}: ${firstError.error}` : "",
  }
}
